import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Shield, AlertCircle, AppWindow, CheckCircle } from 'lucide-react';
import saLogoAi from '../../assets/sa_logo_ai.svg';
import { entraSsoLoginUrl } from '../portal/configurations/AccessManagement/identityProviders/entraConnectionService';
import { oauthAuthApi } from './oauthAuthApi';

function formatClientLabel(clientId: string): string {
  const cleaned = clientId.replace(/[-_]+/g, ' ').trim();
  if (!cleaned) return 'Connected application';
  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
}

const OAuthLoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [error, setError] = useState('');

  const clientId = searchParams.get('client_id')?.trim() || '';
  const scope = searchParams.get('scope')?.trim() || '';
  const oauthTransaction = searchParams.get('oauth_transaction')?.trim() || '';
  const clientLabel = formatClientLabel(clientId);
  const scopes = useMemo(() => scope.split(/\s+/).filter(Boolean), [scope]);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const isFormValid = Boolean(email && password && isValidEmail(email));
  const busy = isLoading || ssoLoading;

  const followRedirect = (redirect: string) => {
    window.location.assign(redirect);
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!isFormValid || busy) return;
    if (!oauthTransaction) {
      setError('This sign-in must start from an authorized application.');
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      const result = await oauthAuthApi.login(email, password, oauthTransaction);
      followRedirect(result.redirect);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSso = () => {
    if (busy) return;
    if (!oauthTransaction) {
      setError('This sign-in must start from an authorized application.');
      return;
    }
    setError('');
    const popup = window.open(entraSsoLoginUrl(undefined, 'login'), 'entra-sso-login', 'width=520,height=720');
    if (!popup) {
      setError('Allow popups to sign in with Microsoft.');
      return;
    }
    setSsoLoading(true);
    const finish = (message?: string) => {
      setSsoLoading(false);
      if (message) setError(message);
    };
    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || event.origin !== window.location.origin) return;
      if (data.type !== 'ENTRA_SSO_LOGIN' && data.type !== 'ENTRA_SSO_TEST') return;
      window.removeEventListener('message', onMessage);
      if (!data.ok || !data.payload?.token) {
        finish(data.message || 'Microsoft sign-in was cancelled or no matching Selfic user was found.');
        return;
      }
      oauthAuthApi
        .complete(data.payload.token, oauthTransaction)
        .then((result) => followRedirect(result.redirect))
        .catch((err) => finish(err instanceof Error ? err.message : 'Login failed'));
    };
    window.addEventListener('message', onMessage);
    const timer = window.setInterval(() => {
      if (!popup.closed) return;
      window.clearInterval(timer);
      window.removeEventListener('message', onMessage);
      finish();
    }, 500);
  };

  return (
    <div className="min-h-screen bg-[#07080c] text-white flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -left-16 w-[28rem] h-[28rem] rounded-full bg-indigo-600/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[32rem] h-[32rem] rounded-full bg-cyan-500/10 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.16) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
      </div>

      <div
        className={`relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="lg:hidden mb-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={saLogoAi} alt="Selfic" className="w-9 h-9 object-contain" />
            <div>
              <p className="text-sm font-semibold leading-none">Selfic OAuth</p>
              <p className="text-[11px] text-neutral-500 mt-1">Identity for connected apps</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-neutral-300">
            <Shield className="w-3 h-3 text-emerald-400" />
            OAuth 2.0
          </span>
        </div>

        <div className="grid lg:grid-cols-2 overflow-hidden rounded-3xl border border-white/10 bg-neutral-900/70 shadow-2xl backdrop-blur-xl">
          <aside className="relative hidden lg:flex flex-col justify-between gap-10 p-10 bg-gradient-to-br from-indigo-500/15 via-transparent to-cyan-500/10">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img src={saLogoAi} alt="Selfic" className="w-10 h-10 object-contain" />
                <div>
                  <p className="text-sm font-semibold leading-none">Selfic OAuth</p>
                  <p className="text-xs text-neutral-400 mt-1">Identity for connected apps</p>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-neutral-300">
                <Shield className="w-3 h-3 text-emerald-400" />
                OAuth 2.0
              </span>
            </div>

            <div>
              <div className="flex items-center gap-4 mb-7">
                <div className="w-16 h-16 rounded-2xl bg-neutral-950/70 border border-white/10 flex items-center justify-center">
                  <img src={saLogoAi} alt="" className="w-10 h-10 object-contain" />
                </div>
                <div className="w-14 flex items-center" aria-hidden="true">
                  <span className="h-px flex-1 bg-white/15" />
                  <span className="mx-1.5 w-1.5 h-1.5 rounded-full bg-cyan-300 animate-pulse" />
                  <span className="h-px flex-1 bg-white/15" />
                </div>
                <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                  {clientId ? (
                    <span className="text-xl font-semibold text-white">{clientLabel.slice(0, 1)}</span>
                  ) : (
                    <AppWindow className="w-7 h-7 text-neutral-400" />
                  )}
                </div>
              </div>

              <h1 className="text-3xl font-semibold tracking-tight leading-tight">Sign in to continue</h1>
              <p className="mt-3 text-sm text-neutral-400 leading-relaxed max-w-sm">
                {clientId
                  ? `${clientLabel} wants to use your Selfic identity.`
                  : 'Use your Selfic account to authorize a connected application.'}
              </p>

              {scopes.length > 0 && (
                <div className="mt-6">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-neutral-500 mb-2.5">Requested access</p>
                  <div className="flex flex-wrap gap-1.5">
                    {scopes.map((item) => (
                      <span
                        key={item}
                        className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-neutral-300"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2.5">
              <p className="text-sm font-medium text-white flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                Protected sign-in
              </p>
              <p className="flex items-start gap-2 text-sm text-neutral-400">
                <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                Selfic never shares your password with the requesting application.
              </p>
            </div>
          </aside>

          <section className="p-6 sm:p-8 lg:p-10">
            <div className="lg:hidden mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl bg-neutral-950/70 border border-white/10 flex items-center justify-center">
                  <img src={saLogoAi} alt="" className="w-7 h-7 object-contain" />
                </div>
                <div className="w-8 flex items-center" aria-hidden="true">
                  <span className="h-px flex-1 bg-white/15" />
                  <span className="mx-1 w-1.5 h-1.5 rounded-full bg-cyan-300" />
                  <span className="h-px flex-1 bg-white/15" />
                </div>
                <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  {clientId ? (
                    <span className="text-base font-semibold">{clientLabel.slice(0, 1)}</span>
                  ) : (
                    <AppWindow className="w-5 h-5 text-neutral-400" />
                  )}
                </div>
              </div>
              <h1 className="text-2xl font-semibold tracking-tight">Sign in to continue</h1>
              <p className="mt-1.5 text-sm text-neutral-400 leading-relaxed">
                {clientId
                  ? `${clientLabel} wants to use your Selfic identity.`
                  : 'Use your Selfic account to authorize a connected application.'}
              </p>
              {scopes.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {scopes.map((item) => (
                    <span
                      key={item}
                      className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-neutral-300"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="hidden lg:block mb-7">
              <h2 className="text-2xl font-semibold">Welcome back</h2>
              <p className="text-sm text-neutral-400 mt-1">Enter your credentials to authorize this application</p>
            </div>

            {error && (
              <div className="mb-5 p-3.5 rounded-xl border border-red-500/20 bg-red-500/10 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}

            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="space-y-1.5">
                <label htmlFor="oauth-email" className="block text-sm text-neutral-300">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <input
                    id="oauth-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl border border-neutral-700 bg-neutral-950/70 pl-11 pr-4 py-3 text-sm text-white placeholder:text-neutral-500 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="oauth-password" className="block text-sm text-neutral-300">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <input
                    id="oauth-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-xl border border-neutral-700 bg-neutral-950/70 pl-11 pr-12 py-3 text-sm text-white placeholder:text-neutral-500 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-neutral-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="rounded border-neutral-600 bg-neutral-900 text-indigo-500 focus:ring-indigo-500"
                />
                Remember me
              </label>

              <button
                type="submit"
                disabled={!isFormValid || busy}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition ${
                  isFormValid && !busy
                    ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                    : 'bg-neutral-800 text-neutral-500 cursor-not-allowed'
                }`}
              >
                {isLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Authorizing...
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <div className="my-6 flex items-center gap-3">
              <div className="h-px flex-1 bg-neutral-800" />
              <span className="text-xs uppercase tracking-wider text-neutral-500">or</span>
              <div className="h-px flex-1 bg-neutral-800" />
            </div>

            <button
              type="button"
              onClick={handleSso}
              disabled={busy}
              className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border border-neutral-700 bg-neutral-950/60 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {ssoLoading ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <MicrosoftMark />
              )}
              {ssoLoading ? 'Waiting for Microsoft...' : 'Continue with Microsoft'}
            </button>

            <p className="mt-5 text-center text-xs text-neutral-500 lg:hidden">
              Selfic never shares your password with the requesting application.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

const MicrosoftMark = () => (
  <svg viewBox="0 0 23 23" className="w-4 h-4" aria-hidden="true">
    <path fill="#f35325" d="M1 1h10v10H1z" />
    <path fill="#81bc06" d="M12 1h10v10H12z" />
    <path fill="#05a6f0" d="M1 12h10v10H1z" />
    <path fill="#ffba08" d="M12 12h10v10H12z" />
  </svg>
);

export default OAuthLoginPage;
